import { RangeWithArray } from '@Config/types'
import {
  KenoResultLocation,
  RapidDrawingResultFormatted,
} from '@interaction-gaming/game-data-service-browser-client'

const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const simulateRandomNetworkTime = (min: number = 1, max: number = 2000) => {
  const randMS = min + Math.random() * (max - min)
  return sleep(randMS)
}

const mapRangeWithArrayToArray = (range: RangeWithArray, increment: number = 1) => {
  const setValues = new Set(range.otherOptions)

  for (let i = range.min; i <= range.max; i += increment) {
    setValues.add(i)
  }

  const arrayValues: number[] = Array.from(setValues)

  arrayValues.sort((a, b) => a - b)
  return arrayValues
}

const generateRandomNumberArray = (count: number, min: number = 1, max: number = 80) => {
  if (count > max - min + 1) {
    throw new Error(`Cannot generate more than ${max - min} unique numbers`)
  }

  const numbers = new Set<number>()

  while (numbers.size < count) {
    const randomNumber = Math.floor(Math.random() * max) + 1
    numbers.add(randomNumber)
  }

  return Array.from(numbers)
}

const getRandomElementFromArray = <T>(arr: T[]) => {
  if (arr.length === 0) {
    throw new Error(`Cannot find element of array length 0`)
  }
  const randomIndex = Math.floor(Math.random() * arr.length)
  return arr[randomIndex]
}

const shuffleArray = <T>(arr: T[]): T[] => {
  const array = [...arr]
  for (let i = array.length - 1; i > 0; i--) {
    const randomIndex = Math.floor(Math.random() * (i + 1))

    const temp = array[i]
    array[i] = array[randomIndex]
    array[randomIndex] = temp
  }
  return array
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',

  // @ts-expect-error not in typescript
  trailingZeroDisplay: 'stripIfInteger',
})

const convertCentsNumberToDollarString = (cents: number) => {
  if (isNaN(cents)) {
    throw new Error('Input must be a number')
  }

  return currencyFormatter.format(cents / 100)
}

const toKebabCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[^a-z0-9-]/g, '')
}

const toShortDate = (isoDateString: string) => {
  return new Date(isoDateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const getWinningNumbersFromResult = (
  resultLocation: KenoResultLocation,
  drawingResult: RapidDrawingResultFormatted,
): number[] => {
  switch (resultLocation) {
    case KenoResultLocation.PRIMARY_WINNING_NUMBERS:
      return drawingResult.primary?.winningNumbers || []
    case KenoResultLocation.PRIMARY_MULTIPLIERS:
      return drawingResult.primary?.multipliers || []
    case KenoResultLocation.PRIMARY_BONUS_NUMBERS:
      return drawingResult.primary?.bonusNumbers || []
    case KenoResultLocation.SECONDARY_WINNING_NUMBERS:
      return drawingResult.secondary?.winningNumbers || []
    case KenoResultLocation.SECONDARY_MULTIPLIERS:
      return drawingResult.secondary?.multipliers || []
    case KenoResultLocation.SECONDARY_BONUS_NUMBERS:
      return drawingResult.secondary?.bonusNumbers || []
    default:
      return []
  }
}

const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180

const zeroPadNumber = (num: number) => String(num).padStart(2, '0')

const noop = () => {}

const getMultiplierDisplayValue = (
  value: number,
  noMultiplierDisplay?: string,
  xFirst: boolean = false,
): string => {
  const multString = xFirst ? `x${value}` : `${value}x`
  return value === 1 && noMultiplierDisplay ? noMultiplierDisplay : multString
}

export const Utils = {
  convertCentsNumberToDollarString,
  degreesToRadians,
  generateRandomNumberArray,
  getMultiplierDisplayValue,
  getRandomElementFromArray,
  getWinningNumbersFromResult,
  mapRangeWithArrayToArray,
  noop,
  simulateRandomNetworkTime,
  shuffleArray,
  sleep,
  toKebabCase,
  toShortDate,
  zeroPadNumber,
}
